import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import GetInTouch from '../components/sections/GetInTouch';
import { services, serviceDetails } from '../mock';
import { ArrowRight, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const ServicesPage = () => {
  const [active, setActive] = useState(null);
  const details = active ? serviceDetails[active.slug] : null;

  return (
    <>
      <PageBanner title="Our Services" subtitle="Thoughtful health and social care support, tailored to each person and the people who care about them." crumb="Services" variant="services" images={["/generated/svc-homecare.png", "/generated/whychoose.png", "/generated/svc-companion.png"]} />

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s) => (
            <div key={s.num} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col">
              <div className="relative h-52 overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-brand-orange text-white font-heading font-bold flex items-center justify-center text-sm">{s.num}</div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 leading-snug">{s.title}</h3>
                <p className="font-body text-sm text-slate-600 mb-4 leading-relaxed flex-1">{s.desc}</p>
                <button
                  type="button"
                  onClick={() => setActive(s)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:text-brand-orange hover:gap-3 transition-all duration-300 self-start"
                >
                  Read More <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20 lg:pb-28 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-brand-blue-light rounded-3xl p-10 md:p-14">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 mb-6 text-center">What working with us feels like</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {['Care plans built around the individual','Skilled, Garda-vetted professionals','Flexible support, day or night, across Ireland','Quick, dependable staffing cover','A calm, respectful, dignity-first approach','A free, no-pressure first conversation'].map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-orange flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" /></div>
                  <span className="font-body text-slate-700">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GetInTouch />

      {/* Service detail popup */}
      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <div className="relative h-48 w-full">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-dark/70 to-transparent" />
                <span className="absolute top-4 left-4 w-11 h-11 rounded-full bg-brand-orange text-white font-heading font-bold flex items-center justify-center text-sm">{active.num}</span>
              </div>

              <div className="p-6 sm:p-8">
                <DialogHeader className="text-left space-y-3">
                  <DialogTitle className="font-heading font-bold text-2xl text-slate-900">{active.title}</DialogTitle>
                  <DialogDescription className="font-body text-slate-600 leading-relaxed text-[15px]">
                    {details?.overview || active.desc}
                  </DialogDescription>
                </DialogHeader>

                {details?.points && (
                  <div className="mt-6 space-y-3">
                    {details.points.map((p) => (
                      <div key={p} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-brand-blue-light flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-brand-blue" />
                        </span>
                        <span className="font-body text-slate-700 text-[15px]">{p}</span>
                      </div>
                    ))}
                  </div>
                )}

                <DialogFooter className="mt-8 sm:justify-start">
                  <Button asChild className="bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold h-11 px-7 rounded-full">
                    <Link to="/contact" onClick={() => setActive(null)} className="inline-flex items-center gap-2">
                      Enquire about this <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ServicesPage;
